import React, { useState } from 'react';
import {
  X,
  BarChart3,
  PieChart,
  Users,
  MapPin,
  DollarSign,
  Brain,
  TrendingUp,
  Target,
  Zap,
  ChevronDown,
  ChevronUp,
  Activity,
} from 'lucide-react';
import { cn } from '../../lib/utils';

interface MacroCluster {
  name: string;
  share_of_personas_pct: number;
  defining_hook: string;
  subsegments_included: string[];
  relevant_traits: string[];
}

interface QuantRollups {
  ocean_avg_all: {
    O: number;
    C: number;
    E: number;
    A: number;
    N: number;
  };
  categorical_distributions: {
    fandom_level: Record<string, number>;
    faith_guided_pct: number;
    time_pressure_pct: number;
  };
}

interface NewDemographicFormat {
  exec_summary: string;
  macro_clusters: MacroCluster[];
  quant_rollups: QuantRollups;
}

interface VisualCard {
  type: string;
  title: string;
  primary_stat: string;
  subtitle?: string;
  secondary_stats?: Array<{
    label: string;
    value: string;
  }>;
  color_theme?: string;
  icon?: string;
  // For insight cards
  insight?: string;
  action?: string;
  impact?: string;
  confidence?: string;
  // For segment cards
  age_range?: string;
  gender_split?: Record<string, number>;
  income_range?: string;
  persona_count?: number;
  segment_name?: string;
  top_behaviors?: string[];
  top_locations?: string[];
}

interface SegmentAnalyticsData extends Partial<NewDemographicFormat> {
  segment_overviews?: Array<{
    age_range?: string;
    avg_age?: number;
    education_levels?: string[];
    gender_split?: Record<string, number>;
    income_range?: string;
    persona_count?: number;
    segment_name?: string;
    top_behaviors?: string[];
    top_locations?: string[];
    top_occupations?: string[];
    top_traits?: string[];
  }>;
  visual_cards?: VisualCard[];
  summary?: {
    age_range?: string;
    avg_personas_per_segment?: number;
    most_common_behaviors?: string[];
    most_common_locations?: string[];
    most_common_traits?: string[];
  };
  total_personas?: number;
  total_segments?: number;
  visualizations?: Record<string, any>;
  // Old format fields
  segment_name?: string;
  age_analysis?: any;
  income_analysis?: any;
  locations?: any[];
}

interface SegmentAnalyticsModalProps {
  isOpen: boolean;
  onClose: () => void;
  segmentName: string;
  analyticsData?: SegmentAnalyticsData;
}

const SegmentAnalyticsModal: React.FC<SegmentAnalyticsModalProps> = ({
  isOpen,
  onClose,
  segmentName,
  analyticsData,
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'insights' | 'visualizations'>(
    'overview',
  );
  const [expandedClusters, setExpandedClusters] = useState<Record<number, boolean>>({});

  // Check if data is in new format
  const isNewFormat = Boolean(analyticsData?.exec_summary);

  if (!isOpen || !analyticsData) return null;

  const getIconForCard = (card: VisualCard) => {
    switch (card.type) {
      case 'demographic':
        if (card.title.toLowerCase().includes('age')) return <Users className="w-5 h-5" />;
        if (card.title.toLowerCase().includes('location')) return <MapPin className="w-5 h-5" />;
        if (card.title.toLowerCase().includes('persona')) return <Users className="w-5 h-5" />;
        return <BarChart3 className="w-5 h-5" />;
      case 'insight':
        if (card.title.toLowerCase().includes('income')) return <DollarSign className="w-5 h-5" />;
        if (card.title.toLowerCase().includes('geographic')) return <MapPin className="w-5 h-5" />;
        if (card.title.toLowerCase().includes('behavioral')) return <Brain className="w-5 h-5" />;
        return <TrendingUp className="w-5 h-5" />;
      case 'segment':
        return <Users className="w-5 h-5" />;
      default:
        return <BarChart3 className="w-5 h-5" />;
    }
  };

  const getColorClasses = (colorTheme?: string) => {
    switch (colorTheme) {
      case '#3B82F6':
        return 'bg-blue-50 border-blue-200 text-blue-900';
      case '#10B981':
        return 'bg-green-50 border-green-200 text-green-900';
      case '#F59E0B':
        return 'bg-amber-50 border-amber-200 text-amber-900';
      case '#8B5CF6':
        return 'bg-purple-50 border-purple-200 text-purple-900';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-900';
    }
  };

  const toggleClusterExpansion = (index: number) => {
    setExpandedClusters((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const renderOceanScore = (scores: QuantRollups['ocean_avg_all']) => {
    const traits = [
      { key: 'O', label: 'Openness', color: 'bg-blue-500' },
      { key: 'C', label: 'Conscientiousness', color: 'bg-green-500' },
      { key: 'E', label: 'Extraversion', color: 'bg-yellow-500' },
      { key: 'A', label: 'Agreeableness', color: 'bg-purple-500' },
      { key: 'N', label: 'Neuroticism', color: 'bg-red-500' },
    ];

    return (
      <div className="space-y-3">
        {traits.map((trait) => (
          <div key={trait.key} className="flex items-center gap-3">
            <span className="w-32 text-sm font-medium text-gray-700">{trait.label}</span>
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div
                className={cn(trait.color, 'h-2 rounded-full transition-all duration-500')}
                style={{ width: `${scores[trait.key as keyof typeof scores] * 100}%` }}
              />
            </div>
            <span className="text-sm font-medium text-gray-600 w-12">
              {(scores[trait.key as keyof typeof scores] * 100).toFixed(0)}%
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderFandomDistribution = (distribution: Record<string, number>) => {
    const total = Object.values(distribution).reduce((sum, val) => sum + val, 0);
    const colors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-green-500', 'bg-blue-500'];
    const orderedKeys = ['non_fan', 'aware_not_engaged', 'casual', 'fan', 'die_hard'];
    const labels = {
      non_fan: 'Non-Fan',
      aware_not_engaged: 'Aware',
      casual: 'Casual',
      fan: 'Fan',
      die_hard: 'Die Hard',
    };

    return (
      <div className="space-y-3">
        {orderedKeys.map((key, index) => {
          const value = distribution[key] || 0;
          const percentage = total > 0 ? (value / total) * 100 : 0;

          if (value === 0) return null;

          return (
            <div key={key} className="flex items-center gap-3">
              <span className="w-24 text-sm font-medium text-gray-700">
                {labels[key as keyof typeof labels]}
              </span>
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div
                  className={cn(colors[index], 'h-2 rounded-full transition-all duration-500')}
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className="text-sm font-medium text-gray-600 w-16">
                {value} ({percentage.toFixed(0)}%)
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderNewFormatOverview = () => {
    const data = analyticsData as NewDemographicFormat;

    return (
      <div className="space-y-6">
        {/* Executive Summary */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Target className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Executive Summary</h3>
              <p className="text-gray-700 leading-relaxed">{data.exec_summary}</p>
            </div>
          </div>
        </div>

        {/* Macro Clusters */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Audience Clusters
          </h3>
          <div className="space-y-4">
            {data.macro_clusters.map((cluster, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                <div
                  className="p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => toggleClusterExpansion(index)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-lg font-semibold text-gray-900">{cluster.name}</h4>
                        <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
                          {cluster.share_of_personas_pct}%
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 italic">"{cluster.defining_hook}"</p>
                    </div>
                    <button className="p-2">
                      {expandedClusters[index] ? (
                        <ChevronUp className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      )}
                    </button>
                  </div>
                </div>

                {expandedClusters[index] && (
                  <div className="p-4 bg-white border-t border-gray-200">
                    <div className="space-y-4">
                      <div>
                        <h5 className="font-medium text-sm text-gray-700 mb-2">
                          Subsegments Included:
                        </h5>
                        <div className="flex flex-wrap gap-2">
                          {cluster.subsegments_included.map((segment, segIndex) => (
                            <span
                              key={segIndex}
                              className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded"
                            >
                              {segment}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h5 className="font-medium text-sm text-gray-700 mb-2">
                          Key Characteristics:
                        </h5>
                        <ul className="space-y-1">
                          {cluster.relevant_traits.map((trait, traitIndex) => (
                            <li
                              key={traitIndex}
                              className="flex items-start gap-2 text-sm text-gray-600"
                            >
                              <span className="text-blue-500 mt-1">•</span>
                              <span>{trait}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Quantitative Metrics */}
        {data.quant_rollups && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* OCEAN Scores */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Brain className="w-5 h-5 text-purple-600" />
                <h4 className="font-semibold text-gray-900">Personality Profile (OCEAN)</h4>
              </div>
              {renderOceanScore(data.quant_rollups.ocean_avg_all)}
            </div>

            {/* Categorical Distributions */}
            <div className="space-y-6">
              {/* Fandom Level */}
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Activity className="w-5 h-5 text-green-600" />
                  <h4 className="font-semibold text-gray-900">Engagement Distribution</h4>
                </div>
                {renderFandomDistribution(
                  data.quant_rollups.categorical_distributions.fandom_level,
                )}
              </div>

              {/* Other Metrics */}
              <div className="grid grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-4 h-4 text-orange-600" />
                    <span className="text-sm font-medium text-gray-700">Faith Guided</span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {data.quant_rollups.categorical_distributions.faith_guided_pct}%
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-4 h-4 text-red-600" />
                    <span className="text-sm font-medium text-gray-700">Time Pressure</span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {data.quant_rollups.categorical_distributions.time_pressure_pct}%
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderOverviewTab = () => {
    const demographicCards =
      analyticsData.visual_cards?.filter((card) => card.type === 'demographic') || [];
    const segmentCards =
      analyticsData.visual_cards?.filter((card) => card.type === 'segment') || [];

    return (
      <div className="space-y-6">
        {/* Key Demographics */}
        {demographicCards.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Demographics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {demographicCards.map((card, index) => (
                <div
                  key={index}
                  className={cn('p-4 rounded-lg border', getColorClasses(card.color_theme))}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {getIconForCard(card)}
                      <h4 className="font-medium text-sm">{card.title}</h4>
                    </div>
                  </div>
                  <div className="text-2xl font-bold mb-1">{card.primary_stat}</div>
                  {card.subtitle && <div className="text-sm opacity-75 mb-3">{card.subtitle}</div>}
                  {card.secondary_stats && card.secondary_stats.length > 0 && (
                    <div className="space-y-1">
                      {card.secondary_stats.map((stat, statIndex) => (
                        <div key={statIndex} className="flex justify-between text-xs">
                          <span className="opacity-75">{stat.label}:</span>
                          <span className="font-medium">{stat.value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Segment Details */}
        {segmentCards.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Segment Details</h3>
            <div className="space-y-4">
              {segmentCards.map((card, index) => (
                <div
                  key={index}
                  className={cn('p-6 rounded-lg border', getColorClasses(card.color_theme))}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">{getIconForCard(card)}</div>
                    <div className="flex-1 space-y-4">
                      <div>
                        <h4 className="font-semibold text-lg mb-2">{card.segment_name}</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          {card.age_range && (
                            <div>
                              <span className="font-medium">Age Range:</span>
                              <div>{card.age_range}</div>
                            </div>
                          )}
                          {card.income_range && (
                            <div>
                              <span className="font-medium">Income:</span>
                              <div>{card.income_range}</div>
                            </div>
                          )}
                          {card.persona_count && (
                            <div>
                              <span className="font-medium">Personas:</span>
                              <div>{card.persona_count}</div>
                            </div>
                          )}
                          {card.gender_split && (
                            <div>
                              <span className="font-medium">Gender Split:</span>
                              <div>
                                {Object.entries(card.gender_split).map(([gender, count]) => (
                                  <div key={gender} className="text-xs">
                                    {gender}: {count}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {card.top_behaviors && card.top_behaviors.length > 0 && (
                        <div>
                          <span className="font-medium text-sm mb-2 block">Top Behaviors:</span>
                          <div className="space-y-1">
                            {card.top_behaviors.slice(0, 3).map((behavior, behaviorIndex) => (
                              <div key={behaviorIndex} className="text-xs bg-white/50 p-2 rounded">
                                {behavior}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderInsightsTab = () => {
    const insightCards =
      analyticsData.visual_cards?.filter((card) => card.type === 'insight') || [];

    return (
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">AI-Generated Insights</h3>
        {insightCards.length > 0 ? (
          <div className="space-y-4">
            {insightCards.map((card, index) => (
              <div
                key={index}
                className={cn('p-6 rounded-lg border', getColorClasses(card.color_theme))}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">{getIconForCard(card)}</div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-lg mb-3">{card.title}</h4>
                    {card.insight && (
                      <div className="mb-4">
                        <span className="font-medium text-sm">Insight:</span>
                        <p className="text-sm mt-1">{card.insight}</p>
                      </div>
                    )}
                    {card.action && (
                      <div className="mb-4">
                        <span className="font-medium text-sm">Recommended Action:</span>
                        <p className="text-sm mt-1">{card.action}</p>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-xs">
                      {card.impact && (
                        <div>
                          <span className="font-medium">Impact:</span> {card.impact}
                        </div>
                      )}
                      {card.confidence && (
                        <div>
                          <span className="font-medium">Confidence:</span>
                          <span
                            className={cn(
                              'ml-1 px-2 py-1 rounded-full',
                              card.confidence === 'High'
                                ? 'bg-green-100 text-green-800'
                                : card.confidence === 'Medium'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800',
                            )}
                          >
                            {card.confidence}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Brain className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No insights available for this segment</p>
          </div>
        )}
      </div>
    );
  };

  const renderVisualizationsTab = () => {
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Visualizations</h3>
        <div className="text-center py-8 text-gray-500">
          <PieChart className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Chart visualizations coming soon</p>
          <p className="text-sm mt-1">Raw data available in console</p>
        </div>
        {analyticsData.visualizations && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <details>
              <summary className="cursor-pointer font-medium">View Raw Visualization Data</summary>
              <pre className="mt-2 text-xs overflow-auto">
                {JSON.stringify(analyticsData.visualizations, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Segment Analytics</h2>
            <p className="text-sm text-gray-600 mt-1">{segmentName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <nav className="flex space-x-8 px-6">
            {[
              { key: 'overview', label: 'Overview', icon: BarChart3 },
              { key: 'insights', label: 'AI Insights', icon: Brain },
              { key: 'visualizations', label: 'Charts', icon: PieChart },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key as any)}
                className={cn(
                  'flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors',
                  activeTab === key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700',
                )}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {activeTab === 'overview' &&
            (isNewFormat ? renderNewFormatOverview() : renderOverviewTab())}
          {activeTab === 'insights' && renderInsightsTab()}
          {activeTab === 'visualizations' && renderVisualizationsTab()}
        </div>
      </div>
    </div>
  );
};

export default SegmentAnalyticsModal;
